import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CopyToClipboard } from "../../Utils";
import { fetchChecklists } from "../../../api/quality/checklist";
import { ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";

export const ChecklistSection = ({ vehicleId, className = "" }) => {
  const { t } = useTranslation();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (vehicleId) {
      fetchChecklist();
    }
  }, [vehicleId]);

  const fetchChecklist = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchChecklists({
        filters: [{ name: "vehicle_id", value: vehicleId }],
        per_page: 1,
        page: 1,
        sortBy: "created_at",
        sort: "DESC",
      });
      const checklistList = response?.data || [];
      setChecklist(checklistList.length > 0 ? checklistList[0] : null);
    } catch (err) {
      console.error("Error fetching checklist:", err);
      setError("Failed to load checklist data");
    } finally {
      setLoading(false);
    }
  };

  if (!vehicleId) {
    return null;
  }

  if (loading) {
    return (
      <div className={`shadow sm:overflow-hidden sm:rounded-md mt-5 ${className}`}>
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <div className="animate-pulse flex space-x-4 w-full">
              <div className="rounded-lg bg-purple-200 h-10 w-10"></div>
              <div className="flex-1 space-y-2 py-1">
                <div className="h-4 bg-purple-200 rounded w-1/4"></div>
                <div className="h-3 bg-purple-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white px-6 py-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`shadow sm:overflow-hidden sm:rounded-md mt-5 ${className}`}>
        <div className="bg-red-50 px-6 py-4 border-b border-red-200">
          <div className="flex items-center">
            <div className="text-red-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-red-900">{t("Error Loading Checklist")}</h3>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!checklist) {
    return (
      <div className={`shadow sm:overflow-hidden sm:rounded-md mt-5 ${className}`}>
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <ClipboardDocumentCheckIcon className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">{t("Latest Internal Control")}</h3>
                <p className="text-sm text-gray-600">{t("Most recent checklist for this vehicle")}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white px-6 py-6">
          <div className="text-center py-4">
            <p className="text-sm text-gray-500">{t("No checklists found")}</p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-blue-100 text-blue-800";
      case "finished":
        return "bg-green-100 text-green-800";
      case "in_progress":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "approved":
        return t("Approved");
      case "finished":
        return t("Finished");
      case "in_progress":
        return t("In Progress");
      default:
        return t("Open");
    }
  };

  return (
    <div className={`shadow sm:overflow-hidden sm:rounded-md mt-5 ${className}`}>
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <ClipboardDocumentCheckIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="ml-4">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900">{t("Latest Internal Control")}</h3>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                    checklist.status
                  )}`}
                >
                  {getStatusText(checklist.status)}
                </span>
              </div>
              <p className="text-sm text-gray-600">{t("Most recent checklist for this vehicle")}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("Checklist Number")}</dt>
            <dd className="mt-1 text-sm text-gray-900 flex items-center">
              {checklist.number || "-"}
              {checklist.number && <CopyToClipboard value={checklist.number} />}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("Title")}</dt>
            <dd className="mt-1 text-sm text-gray-900">{checklist.title || "-"}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("Date")}</dt>
            <dd className="mt-1 text-sm text-gray-900">{checklist.date || "-"}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("Responsible")}</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {checklist.responsible_executive?.displayTitle || "-"}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("Items Approved")}</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {checklist.items?.filter((item) => item.isApproved).length || 0} /{" "}
              {checklist.items?.length || 0}
            </dd>
          </div>
        </div>
      </div>
    </div>
  );
};

